import DataTable from "@/components/data-table";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn, extractErrorMessage } from "@/lib/utils";
import { PermissionValues, permissionsArraySchema } from "@/pages/role/constants";
import { MemoizedPermissionInput } from "@/pages/role/permission-input";
import { createFileRoute } from "@tanstack/react-router";
import { useActiveWorkspace } from "@wacht/tanstack-router";
import { ColDef } from "ag-grid-community";
import { EllipsisVertical, Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import * as z from "zod";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ConfirmActionPopup } from "@/components/popups/confirm-action";

export const Route = createFileRoute("/_private/roles")({
  component: RouteComponent,
});

const roleFormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  permissions: permissionsArraySchema,
});

type RoleFormValues = z.infer<typeof roleFormSchema>;

function RouteComponent() {
  const { getRoles, createRole, deleteRole } = useActiveWorkspace();
  const [roles, setRoles] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<any>(null);

  const getRolesRef = useRef(getRoles);
  const createRoleRef = useRef(createRole);
  const deleteRoleRef = useRef(deleteRole);

  useEffect(() => {
    getRolesRef.current = getRoles;
    createRoleRef.current = createRole;
    deleteRoleRef.current = deleteRole;
  }, [getRoles, createRole, deleteRole]);

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getRolesRef.current?.();
      setRoles(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Failed to fetch roles", error);
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const handleDelete = useCallback(async () => {
    if (!selectedRole || !deleteRoleRef.current) return;

    try {
      await deleteRoleRef.current(selectedRole);
      toast.success("Role deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedRole(null);
      fetchRoles();
    } catch (error) {
      const message = extractErrorMessage(error, "Failed to delete role");
      toast.error(message);
    }
  }, [selectedRole, fetchRoles]);

  const rows = useMemo(
    () =>
      roles.map((role) => ({
        ...role,
        _rowId: role?.id ?? role?.name,
      })),
    [roles]
  );

  const columns = useMemo<ColDef<any>[]>(
    () => [
      {
        field: "name",
        headerName: "Role Name",
        minWidth: 200,
        cellRenderer: (params: any) => (
          <span className="text-sm font-medium">{params.value || "—"}</span>
        ),
      },
      {
        field: "permissions",
        headerName: "Permissions",
        minWidth: 300,
        flex: 1,
        valueGetter: (params: any) => {
          const permissions = Array.isArray(params.data?.permissions)
            ? params.data.permissions
            : [];
          return permissions.length ? permissions.join(", ") : "—";
        },
        cellRenderer: (params: any) => {
          const permissions = Array.isArray(params.data?.permissions)
            ? params.data.permissions
            : [];

          if (permissions.length === 0) {
            return <span className="text-sm text-muted-foreground">—</span>;
          }

          const firstPermission = permissions[0];
          const remainingCount = permissions.length - 1;

          if (remainingCount === 0) {
            return <span className="text-sm">{firstPermission}</span>;
          }

          return (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1 cursor-help">
                    <span className="text-sm">{firstPermission}</span>
                    <span className="text-sm text-muted-foreground">
                      +{remainingCount}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="max-w-md">
                  <div className="space-y-1">
                    <div className="font-medium text-sm mb-2">
                      All Permissions ({permissions.length})
                    </div>
                    <div className="space-y-1">
                      {permissions.map((permission: string, index: number) => (
                        <div key={index} className="text-xs">
                          {permission}
                        </div>
                      ))}
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        },
      },
      {
        colId: "actions",
        headerName: "Actions",
        maxWidth: 80,
        minWidth: 80,
        width: 80,
        filter: false,
        sortable: false,
        suppressMovable: true,
        pinned: "right",
        cellRenderer: (params: any) => {
          const role = params.data;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 hover:bg-gray-100"
                >
                  <EllipsisVertical className="h-4 w-4 text-gray-600" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => {
                    setSelectedRole(role);
                    setDeleteDialogOpen(true);
                  }}
                  className="text-red-600 focus:text-red-600"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    []
  );

  return (
    <div className="space-y-4 w-full">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={cn("text-xl font-semibold")}>Roles</h1>
          <p className="text-sm text-muted-foreground">
            Manage workspace roles and permissions.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchRoles}>
            Refresh
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={() => setShowCreateDialog(true)}
          >
            <Plus size={16} className="mr-2" />
            Create Role
          </Button>
        </div>
      </div>

      <DataTable
        rowData={rows}
        columnDefs={columns}
        height={520}
        loading={loading}
      />

      {/* Create Role Dialog */}
      <CreateRoleDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onSuccess={() => {
          setShowCreateDialog(false);
          fetchRoles();
        }}
        createRole={createRoleRef.current}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmActionPopup
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Role"
        description={
          <span>
            Are you sure you want to delete the role{" "}
            <span className="font-bold">{selectedRole?.name}</span>? This action
            cannot be undone.
          </span>
        }
        actionLabel="Delete"
        icon={Trash2}
        iconColor="text-red-600"
        onConfirm={handleDelete}
      />
    </div>
  );
}

interface CreateRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  createRole?: (name: string, permissions: string[]) => Promise<any>;
}

function CreateRoleDialog({
  open,
  onOpenChange,
  onSuccess,
  createRole,
}: CreateRoleDialogProps) {
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<RoleFormValues>({
    resolver: zodResolver(roleFormSchema),
    defaultValues: {
      name: "",
      permissions: [],
    },
  });

  const onSubmit = async (values: RoleFormValues) => {
    if (!createRole) {
      toast.error("Create role function not available");
      return;
    }

    setIsLoading(true);
    try {
      await createRole(values.name, values.permissions);
      toast.success("Role created successfully");
      form.reset({
        name: "",
        permissions: [],
      });
      onSuccess();
    } catch (error) {
      const message = extractErrorMessage(error, "Failed to create role");
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[min(800px,90vh)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Role</DialogTitle>
          <DialogDescription>
            Create a new role with specific permissions for your workspace.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Role Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter role name..." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="permissions"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Permissions</FormLabel>
                  <FormControl>
                    <MemoizedPermissionInput
                      value={field.value || []}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              form.reset({
                name: "",
                permissions: [],
              });
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={isLoading}
            onClick={form.handleSubmit(onSubmit)}
          >
            {isLoading ? "Creating..." : "Create Role"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default Route;
